import { useState, useEffect, useRef } from "react";
import { thumbUrl } from "@/lib/api";

const SRCSET_WIDTHS = [320, 480, 640, 800, 1000];

// Blur-up + responsive image. A tiny preview (explicit `blur` for user uploads, or a
// w=24 CDN version for Unsplash) paints almost instantly and is shown blurred while
// the sharp image streams in. For Unsplash URLs a srcset/sizes pair lets the browser
// download the size that actually fits the device (much less data on mobile). Meant to
// be dropped inside a `relative` positioned container (it fills it absolutely).
export function SmartImage({ src, alt = "", width = 600, priority = false, blur = "", sizes }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // If the sharp image is already cached it can be `complete` before React attaches
  // the onLoad handler, which would otherwise leave the blur placeholder stuck on top.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) setLoaded(true);
  }, [src, width]);

  if (!src) {
    return <div className="absolute inset-0 flex items-center justify-center text-[#555]">Geen foto</div>;
  }

  const isUnsplash = typeof src === "string" && src.includes("images.unsplash.com");
  const placeholder = blur || (isUnsplash ? thumbUrl(src, 24) : "");
  const full = thumbUrl(src, width);
  const srcSet = isUnsplash
    ? SRCSET_WIDTHS.map((w) => `${thumbUrl(src, w)} ${w}w`).join(", ")
    : undefined;

  return (
    <>
      {placeholder && (
        <div
          aria-hidden
          className={`absolute inset-0 scale-105 bg-cover bg-center transition-opacity duration-700 ${loaded ? "opacity-0" : "opacity-100"}`}
          style={{ backgroundImage: `url("${placeholder}")`, filter: "blur(14px)" }}
        />
      )}
      <img
        ref={imgRef}
        src={full}
        srcSet={srcSet}
        sizes={srcSet ? (sizes || `${width}px`) : undefined}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105 ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
}
